/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form-main");
      // Don't cache optionSelectors - it needs to be fresh after AJAX updates
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      // Track last selected values for deselection
      this.lastSelected = new Map();

      // Initialize progressive disclosure
      this.initializeProgressiveDisclosure();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener("change", this.handleVariantChange.bind(this));
    }

    /**
     * Get fresh option selectors (not cached, since AJAX updates the DOM)
     */
    get optionSelectors() {
      return this.querySelectorAll(".option-selector");
    }

    /**
     * Initialize progressive disclosure - hide child levels until parent is selected
     */
    initializeProgressiveDisclosure() {
      // Check if URL has a variant parameter
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get("variant");

      // Try to load variant from URL
      if (variantId && this.loadVariantFromUrl(variantId)) {
        // Successfully loaded variant from URL - skip clearing
        return;
      }

      // Clear ALL selections and hide child levels
      this.optionSelectors.forEach((selector) => {
        const level = this.getSelectorLevel(selector);

        // Clear ALL selections including Level 1
        this.clearLevelSelection(selector);

        // Hide child levels (Level 2+) without animation on init
        if (level > 1) {
          selector.classList.remove("level-visible");
          selector.style.display = "none";
        }
      });

      // NEW: Auto-select first available option at each level (cascading)
      this.autoSelectCascading();
    }

    /**
     * Auto-select first available option at each level, cascading from Level 1 → 2 → 3
     */
    autoSelectCascading() {
      console.log("🤖 Starting auto-selection cascade...");

      // Process levels in order: 1, 2, 3, etc.
      for (let level = 1; level <= 5; level++) {
        const selector = this.querySelector(
          `.option-selector[data-level="${level}"]`
        );
        if (!selector) break; // No more levels

        console.log(`🔍 Checking Level ${level} for auto-selection...`);

        // Find first available option
        const options = selector.querySelectorAll(".js-option");
        let firstAvailable = null;

        for (const option of options) {
          // Check if option is visible (not hidden by display: none)
          const isVisible =
            option.style.display !== "none" &&
            (!option.nextElementSibling ||
              option.nextElementSibling.style.display !== "none");

          if (isVisible) {
            firstAvailable = option;
            console.log(
              `✅ Found first available option at Level ${level}:`,
              option.value
            );
            break;
          }
        }

        if (firstAvailable) {
          // Auto-select this option
          if (selector.dataset.selectorType === "dropdown") {
            const btn = selector.querySelector(".custom-select__btn");
            if (btn) btn.textContent = firstAvailable.dataset.value;
          } else {
            firstAvailable.checked = true;
            this.lastSelected.set(level, firstAvailable.value);
          }

          console.log(`🎯 Auto-selected Level ${level}:`, firstAvailable.value);

          // Update availability for next level
          this.variant = this.getSelectedVariant();
          this.updateAvailability();

          // Show next level if it exists
          const nextLevel = level + 1;
          const nextSelector = this.querySelector(
            `.option-selector[data-level="${nextLevel}"]`
          );

          if (nextSelector) {
            // Check if next level needs AJAX update
            if (nextSelector.classList.contains("secondary-option-selector")) {
              console.log(
                `📡 Level ${nextLevel} needs AJAX update, stopping cascade here`
              );
              // Trigger AJAX and stop cascade (AJAX will continue cascade)
              this.getProductSection(level).then(() => {
                // After AJAX, continue cascade from next level
                this.autoSelectFromLevel(nextLevel);
              });
              break; // Stop cascade, AJAX will continue it
            } else {
              // Static level - show it and continue cascade
              this.showLevel(nextLevel);
              // Continue to next iteration to auto-select at next level
            }
          } else {
            console.log(`✅ No more levels, cascade complete`);
            break; // No more levels
          }
        } else {
          console.log(
            `❌ No available options at Level ${level}, stopping cascade`
          );
          break; // No available options, stop cascade
        }
      }

      // Final updates
      this.variant = this.getSelectedVariant();
      if (this.variant) {
        this.updateMedia();
        this.updateVariantInput();
      }
      this.updateAddToCartButton();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();

      console.log("🏁 Auto-selection cascade finished");
    }

    /**
     * Auto-select starting from a specific level (used after AJAX)
     */
    autoSelectFromLevel(startLevel) {
      console.log(`🤖 Continuing auto-selection from Level ${startLevel}...`);

      for (let level = startLevel; level <= 5; level++) {
        const selector = this.querySelector(
          `.option-selector[data-level="${level}"]`
        );
        if (!selector) break;

        const options = selector.querySelectorAll(".js-option");
        let firstAvailable = null;

        for (const option of options) {
          const isVisible =
            option.style.display !== "none" &&
            (!option.nextElementSibling ||
              option.nextElementSibling.style.display !== "none");

          if (isVisible) {
            firstAvailable = option;
            break;
          }
        }

        if (firstAvailable) {
          if (selector.dataset.selectorType === "dropdown") {
            const btn = selector.querySelector(".custom-select__btn");
            if (btn) btn.textContent = firstAvailable.dataset.value;
          } else {
            firstAvailable.checked = true;
            this.lastSelected.set(level, firstAvailable.value);
          }

          console.log(`🎯 Auto-selected Level ${level}:`, firstAvailable.value);

          this.variant = this.getSelectedVariant();
          this.updateAvailability();

          const nextLevel = level + 1;
          const nextSelector = this.querySelector(
            `.option-selector[data-level="${nextLevel}"]`
          );

          if (
            nextSelector &&
            !nextSelector.classList.contains("secondary-option-selector")
          ) {
            this.showLevel(nextLevel);
          } else {
            break;
          }
        } else {
          break;
        }
      }

      // Final updates
      this.variant = this.getSelectedVariant();
      if (this.variant) {
        this.updateMedia();
        this.updateVariantInput();
      }
      this.updateAddToCartButton();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
    }

    /**
     * Load variant from URL parameter
     * @param {string} variantId - Variant ID from URL
     * @returns {boolean} True if variant was successfully loaded
     */
    loadVariantFromUrl(variantId) {
      // Find variant by ID
      const variant = this.data.product.variants.find((v) => v.id == variantId);

      if (!variant) {
        console.warn("Variant not found:", variantId);
        return false;
      }

      console.log("Loading variant from URL:", variant);

      // Set selections for each level based on variant options
      let loadSuccess = true;

      this.optionSelectors.forEach((selector, index) => {
        const level = this.getSelectorLevel(selector);
        const optionValue = variant.options[index];

        if (!optionValue) return;

        // Find and select the matching option
        let optionSelected = false;

        if (selector.dataset.selectorType === "dropdown") {
          // For dropdown selectors
          const options = selector.querySelectorAll(".custom-select__option");
          const matchingOption = Array.from(options).find(
            (opt) => opt.dataset.value === optionValue
          );

          if (matchingOption) {
            const btn = selector.querySelector(".custom-select__btn");
            if (btn) {
              btn.textContent = optionValue;
              optionSelected = true;
            }
          }
        } else {
          // For radio button selectors
          const inputs = selector.querySelectorAll('input[type="radio"]');
          const matchingInput = Array.from(inputs).find(
            (input) => input.value === optionValue
          );

          if (matchingInput) {
            matchingInput.checked = true;
            optionSelected = true;

            // Track for deselection
            this.lastSelected.set(level, optionValue);
          }
        }

        if (!optionSelected) {
          console.warn(
            `Could not select option "${optionValue}" at level ${level}`
          );
          loadSuccess = false;
        }

        // Show this level if it's level 2+
        if (level > 1) {
          selector.style.display = "block";
          selector.classList.add("level-visible");
        }
      });

      // Set the variant
      if (loadSuccess) {
        this.variant = variant;

        // Update all UI elements
        this.updateAddToCartButton();
        this.updateAvailability();
        this.updatePrice();
        this.updateWeight();
        this.updateBarcode();
        this.updatePickupAvailability();
        this.updateSku();

        // If Level 2 is dynamic, we might need to trigger AJAX to load correct options
        const level2Selector = this.querySelector(
          '.option-selector[data-level="2"]'
        );
        if (
          level2Selector &&
          level2Selector.classList.contains("secondary-option-selector")
        ) {
          // Trigger AJAX to ensure Level 2 has correct filtered options
          this.getProductSection(1).then(() => {
            // Re-apply selections after AJAX
            this.reapplySelectionsFromVariant(variant);
          });
        }
      }

      return loadSuccess;
    }

    /**
     * Re-apply selections after AJAX update
     * @param {object} variant - Variant to re-apply
     */
    reapplySelectionsFromVariant(variant) {
      this.optionSelectors.forEach((selector, index) => {
        const level = this.getSelectorLevel(selector);
        const optionValue = variant.options[index];

        if (!optionValue || level === 1) return; // Skip level 1, already set

        // Re-select the option
        if (selector.dataset.selectorType === "dropdown") {
          const btn = selector.querySelector(".custom-select__btn");
          if (btn) btn.textContent = optionValue;
        } else {
          const inputs = selector.querySelectorAll('input[type="radio"]');
          const matchingInput = Array.from(inputs).find(
            (input) => input.value === optionValue
          );
          if (matchingInput) {
            matchingInput.checked = true;
            this.lastSelected.set(level, optionValue);
          }
        }

        // Ensure level is visible
        if (level > 1) {
          selector.style.display = "block";
          selector.classList.add("level-visible");
        }
      });

      // Update variant and UI
      this.variant = variant;
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updatePickupAvailability();
      this.updateSku();
    }


    /**
     * Get the level number for a selector
     */
    getSelectorLevel(selector) {
      return parseInt(selector.dataset.level) || 1;
    }

    /**
     * Check if a level has a selection
     */
    hasLevelSelection(selector) {
      return this.hasSelection(selector);
    }

    /**
     * Check if a selector has a selection
     */
    hasSelection(selector) {
      if (selector.dataset.selectorType === "dropdown") {
        // For dropdown selectors, check if a value is selected
        const customSelect = selector.querySelector(".custom-select__btn");
        return customSelect && customSelect.textContent.trim() !== "";
      } else {
        // For radio button selectors, check if any radio is checked
        return selector.querySelector("input:checked") !== null;
      }
    }

    /**
     * Show a specific level with animation
     */
    showLevel(level) {
      const selector = this.querySelector(
        `.option-selector[data-level="${level}"]`
      );
      if (selector) {
        // Remove hiding class if present
        selector.classList.remove("level-hiding");

        // Force display block first, then add visible class for animation
        selector.style.display = "block";

        // Use requestAnimationFrame to ensure display change is applied before animation
        requestAnimationFrame(() => {
          selector.classList.add("level-visible");
        });
      }
    }

    /**
     * Hide a specific level and all subsequent levels with animation
     */
    hideLevelAndSubsequent(fromLevel) {
      // Hide this level and all subsequent levels with animation
      for (let level = fromLevel; level <= 5; level++) {
        const selector = this.querySelector(
          `.option-selector[data-level="${level}"]`
        );
        if (selector) {
          // Always clear selections, even if already hidden
          this.clearLevelSelection(selector);
          this.lastSelected.delete(level);

          // Only animate hiding if currently visible
          if (selector.classList.contains("level-visible")) {
            // Add hiding class to trigger fade-out animation
            selector.classList.add("level-hiding");
            selector.classList.remove("level-visible");

            // After animation completes, hide completely
            setTimeout(() => {
              if (selector.classList.contains("level-hiding")) {
                selector.style.display = "none";
                selector.classList.remove("level-hiding");
              }
            }, 200); // Match the CSS transition duration
          } else {
            // If already hidden, ensure it stays hidden
            selector.style.display = "none";
            selector.classList.remove("level-visible", "level-hiding");
          }
        }
      }
    }

    /**
     * Clear selection in a level
     */
    clearLevelSelection(selector) {
      if (selector.dataset.selectorType === "dropdown") {
        // For dropdown selectors, reset to first option or placeholder
        const customSelect = selector.querySelector(".custom-select__btn");
        if (customSelect) {
          const firstOption = selector.querySelector(".custom-select__option");
          if (firstOption) {
            customSelect.textContent = firstOption.textContent;
          }
        }
      } else {
        // For radio button selectors, uncheck ALL radios (not just first one)
        const checkedInputs = selector.querySelectorAll("input:checked");
        checkedInputs.forEach((input) => {
          input.checked = false;
        });
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Handle progressive disclosure
      const changedSelector = evt.target.closest(".option-selector");
      let changedLevel = null;
      let needsAjaxUpdate = false;

      if (changedSelector) {
        changedLevel = this.getSelectorLevel(changedSelector);

        // Track the selected value for deselection
        const selectedInput = changedSelector.querySelector("input:checked");
        if (selectedInput) {
          this.lastSelected.set(changedLevel, selectedInput.value);
        }

        // Show next level if current level has a selection (for AJAX update or user selection)
        if (this.hasSelection(changedSelector)) {
          const nextLevel = changedLevel + 1;
          const nextSelector = this.querySelector(
            `.option-selector[data-level="${nextLevel}"]`
          );

          // Check if next level exists and needs AJAX update
          if (nextSelector) {
            // If the next level is a dynamic/secondary selector, we need AJAX update
            if (nextSelector.classList.contains("secondary-option-selector")) {
              needsAjaxUpdate = true;
              // Don't show/clear yet - let AJAX response handle it after updating content
              // Hide and clear levels after the AJAX level
              this.hideLevelAndSubsequent(changedLevel + 2);
            } else {
              // For static levels, show and clear immediately
              this.showLevel(nextLevel);
              this.clearLevelSelection(nextSelector);

              // Auto-select first available option at next level (cascading)
              // Note: autoSelectFromLevel handles showing/hiding subsequent levels, so we don't call hideLevelAndSubsequent here
              this.autoSelectFromLevel(nextLevel);
            }
          } else {
            // No next level exists, hide everything after current level
            this.hideLevelAndSubsequent(changedLevel + 1);
          }
        } else {
          // If current level has no selection, hide all subsequent levels
          this.hideLevelAndSubsequent(changedLevel + 1);
        }
      }

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      // Debug: Log variant detection for all levels
      const timestamp = new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      });
      console.log(`\n🕐 [${timestamp}] === Variant Change Debug ===`);
      console.log("Changed level:", changedLevel);
      console.log("Selected options:", this.getSelectedOptions());
      console.log("Found variant:", this.variant);
      if (this.variant) {
        console.log("Variant ID:", this.variant.id);
        console.log("Variant available:", this.variant.available);
      }

      // Call AJAX update if needed (even if variant is null)
      if (needsAjaxUpdate) {
        // Don't call updateAvailability() here - let AJAX callback handle it
        this.getProductSection(changedLevel);

        // Still update UI for current state
        this.updateUrl(evt);
        this.updateAddToCartButton();
        this.updatePrice();
        this.updateWeight();
        this.updateBarcode();
        this.updatePickupAvailability();
        this.updateSku();

        // Exit early - AJAX will handle the rest
        return;
      }

      if (this.variant) {
        this.updateMedia();
        this.updateVariantInput();
      }
      this.updateUrl(evt); // Always update URL, even when variant is null
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product,
          },
        })
      );
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector(".js-product-form-main");
      if (!this.productForm) return;

      this.addBtn =
        this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant
        ? theme.strings.noStock
        : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;
      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);

      // NEW: Hide unavailable options completely
      if (!available) {
        // Hide the radio button
        el.style.display = "none";

        // Hide the label (next sibling)
        if (el.nextElementSibling) {
          el.nextElementSibling.style.display = "none";
        }
      } else {
        // Show available options
        el.style.display = "";

        if (el.nextElementSibling) {
          el.nextElementSibling.style.display = "";
        }
      }

      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityMethod === "selection") {
        // Flag all options as unavailable (except Level 1 to prevent retroactive filtering)
        this.optionSelectors.forEach((selector, selectorIndex) => {
          if (selectorIndex > 0) {
            // Skip Level 1 (index 0)
            selector.querySelectorAll(".js-option").forEach((optionEl) => {
              VariantPicker.updateOptionAvailability(optionEl, false, false);
            });
          }
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          // Skip Level 1 - keep it always available to prevent retroactive filtering
          if (selectorIndex === 0) return;

          // Track which options are available (use Map to avoid last-variant-wins bug)
          const optionAvailability = new Map(); // key: optionValue, value: {exists: bool, available: bool}

          this.data.product.variants.forEach((variant) => {
            // Count how many SELECTED PARENT (non-empty) options match, excluding current selector
            let matchCount = 0;
            let requiredMatches = 0; // How many options we need to match

            variant.options.forEach((option, optionIndex) => {
              // Skip the option we're currently checking availability for
              if (optionIndex === selectorIndex) return;

              // CRITICAL: Only check PARENT levels (before current selector), NOT child levels
              // This prevents retroactive filtering where child selections affect parent availability
              if (optionIndex < selectorIndex) {
                // Only check options that are actually selected (not empty strings)
                if (
                  currVariant.options[optionIndex] &&
                  currVariant.options[optionIndex] !== ""
                ) {
                  requiredMatches += 1;
                  if (option === currVariant.options[optionIndex]) {
                    matchCount += 1;
                  }
                }
              }
            });

            // If all PARENT options match, this variant makes the current option available
            // If no parent selections exist (requiredMatches === 0), show all options
            if (matchCount === requiredMatches) {
              const optionValue = variant.options[selectorIndex];
              const current = optionAvailability.get(optionValue) || {
                exists: false,
                available: false,
              };

              // Mark as exists, and available if ANY matching variant is available
              optionAvailability.set(optionValue, {
                exists: true,
                available: current.available || variant.available, // ANY available variant makes it available
              });
            }
          });

          // Now update the DOM based on collected availability
          optionAvailability.forEach((status, optionValue) => {
            const options = selector.querySelectorAll(".js-option");
            const optionEl = Array.from(options).find((opt) => {
              if (selector.dataset.selectorType === "dropdown") {
                return opt.dataset.value === optionValue;
              }
              return opt.value === optionValue;
            });

            if (optionEl) {
              VariantPicker.updateOptionAvailability(
                optionEl,
                status.exists,
                status.available
              );
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])'
          );
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i] === selectedOptions[i] ||
                  selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable
            );
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder =
        this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hideBackorder = true;

      // Don't hide if no variant selected yet (user still selecting options)
      if (!this.variant) {
        // Keep current visibility state when no complete variant
        return;
      }

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product"
          );
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches("quick-add-drawer")) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery =
          this.mediaGallery || this.section.querySelector("media-gallery");
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price =
        this.price ||
        this.section.querySelector(".product-info__price > .price");
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML =
            this.data.formatted[this.variant.id].compareAtPrice || "";

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          "price--on-sale",
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle("price--sold-out", !this.variant.available);
      }

      this.price.querySelector(".price__default").hidden = !this.variant;
      this.price.querySelector(".price__no-variant").hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights =
        this.weights || this.section.querySelectorAll(".product-info__weight");
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable
          ? this.data.formatted[this.variant.id].weight
          : "";
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes =
        this.barcodes ||
        this.section.querySelectorAll(".product-info__barcode-value");
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id, or clears it if no variant is selected.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;

      // If variant exists, add variant param; otherwise use base URL
      const url = this.variant
        ? `${this.dataset.url}?variant=${this.variant.id}`
        : this.dataset.url;

      window.history.replaceState({}, "", url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(
          ".js-product-form-main, .js-instalments-form"
        );

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const btn = selector.querySelector(".custom-select__btn");
          selectedOptions.push(btn ? btn.textContent.trim() : "");
        } else {
          const checkedInput = selector.querySelector("input:checked");
          selectedOptions.push(checkedInput ? checkedInput.value : "");
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    getProductSection(changedLevel = 1) {
      let mainProductHandle = this.data.product.handle;

      // Find the DYNAMIC level selector that needs updating
      // When Level 1 changes, Level 2 (the dynamic/secondary level) needs AJAX update
      const dynamicLevel = 2; // Hardcoded for now - Level 2 is always the AJAX-updated level
      const dynamicLevelSelector = this.querySelector(
        `.option-selector[data-level="${dynamicLevel}"]`
      );

      if (!dynamicLevelSelector) {
        console.log("No dynamic level to update");
        return Promise.resolve();
      }

      // Get section ID from the dynamic level selector, or fallback to secondary-option-selector
      let mainSectionId = dynamicLevelSelector.dataset.section;
      if (!mainSectionId) {
        const fallbackSection = document.querySelector(
          ".secondary-option-selector"
        );
        mainSectionId = fallbackSection
          ? fallbackSection.dataset.section
          : null;
      }

      if (!mainSectionId) {
        console.warn("No section ID found for AJAX update");
        return Promise.resolve();
      }

      // Build URL with only PARENT level selections (up to and including changed level)
      // This prevents passing stale child selections
      let url = `/products/${mainProductHandle}?section_id=${mainSectionId}`;

      // Add variant ID if available (but we'll rely on level params for filtering)
      if (this.variant && this.variant.id) {
        url += `&variant=${this.variant.id}`;
      }

      // Dynamically get selections for levels up to and including the changed level
      const selectedLevels = {};
      for (let level = 1; level <= changedLevel; level++) {
        const levelSelector = this.querySelector(
          `.option-selector[data-level="${level}"]`
        );
        if (levelSelector) {
          const checkedInput = levelSelector.querySelector("input:checked");
          if (checkedInput) {
            selectedLevels[`level${level}`] = checkedInput.value;
            url += `&level${level}=${encodeURIComponent(checkedInput.value)}`;
          }
        }
      }

      const ajaxTimestamp = new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      });
      console.log(`\n📡 [${ajaxTimestamp}] AJAX URL:`, url);
      console.log(
        "Selected parent levels (up to Level " + changedLevel + "):",
        selectedLevels
      );

      return fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );

          // Try to find the source element - check both 'second-option' (legacy) and dynamic ID
          let source = html.getElementById("second-option");
          if (!source) {
            source = html.querySelector(
              `.option-selector[data-level="${dynamicLevel}"]`
            );
          }

          // Update the dynamic level selector with fresh content
          const destination = dynamicLevelSelector;

          if (source && destination) {
            destination.innerHTML = source.innerHTML;

            // Show the updated level
            this.showLevel(dynamicLevel);

            // Clear the selection (Option A: force user to re-select)
            this.clearLevelSelection(destination);

            // CRITICAL: Hide all levels AFTER the dynamic level
            // This ensures levels beyond the updated one are hidden
            this.hideLevelAndSubsequent(dynamicLevel + 1);
          }

          // Update product data with fresh variant information from server
          const productDataScript = html.querySelector(
            "script[data-product-json]"
          );
          if (productDataScript) {
            try {
              const freshProductData = JSON.parse(
                productDataScript.textContent
              );
              this.data.product = freshProductData;
            } catch (e) {
              console.warn("Could not parse updated product data:", e);
            }
          }

          // Don't recalculate variant here - it will be calculated after auto-selection
          this.variant = null;

          this.updateAvailability();

          // NEW: Auto-select first available option at the dynamic level after AJAX
          this.autoSelectFromLevel(dynamicLevel);

          // Update UI with the auto-selected variant
          this.variant = this.getSelectedVariant();
          if (this.variant) {
            this.updateMedia();
            this.updateVariantInput();

            // Update URL with new variant
            if (this.dataset.updateUrl !== "false") {
              window.history.replaceState(
                {},
                "",
                `${this.dataset.url}?variant=${this.variant.id}`
              );
            }
          } else {
            // Clear URL if no variant
            if (this.dataset.updateUrl !== "false") {
              window.history.replaceState({}, "", this.dataset.url);
            }
          }
          this.updateAddToCartButton();
          this.updatePrice();
          this.updateWeight();
          this.updateBarcode();
          this.updateBackorderText();
          this.updatePickupAvailability();
          this.updateSku();

          // Dispatch variant change event
          this.dispatchEvent(
            new CustomEvent("on:variant:change", {
              bubbles: true,
              detail: {
                form: this.productForm,
                variant: this.variant,
                product: this.data.product,
              },
            })
          );
        });
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      const timestamp = new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      });

      // Debug: Show each level's selection
      console.log(`🔍 [${timestamp}] Selected options breakdown:`);
      selectedOptions.forEach((opt, index) => {
        console.log(`  Level ${index + 1}: "${opt}" (empty: ${opt === ""})`);
      });

      // Check if all levels have selections (no empty strings)
      const allLevelsSelected = selectedOptions.every(
        (option) => option !== ""
      );

      if (!allLevelsSelected) {
        // Option A: Return null if not all levels are selected
        // This prevents showing wrong variants or prices
        console.log("❌ Not all levels selected - returning null");
        return null;
      }

      console.log("✅ All levels selected, searching for variant...");

      // Find the matching variant
      const selectedVariant = this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index])
      );

      if (!selectedVariant) {
        console.log(
          `❌ [${timestamp}] No variant found for options:`,
          selectedOptions
        );
        console.log("Looking for exact match on all variants...");
        this.data.product.variants.forEach((v, vIndex) => {
          console.log(
            `\n=== Variant ${vIndex + 1} (id: ${v.id}, available: ${
              v.available
            }) ===`
          );
          v.options.forEach((variantOpt, optIndex) => {
            const selectedOpt = selectedOptions[optIndex];
            const match = variantOpt === selectedOpt;
            console.log(`  Level ${optIndex + 1}:`);
            console.log(`    Variant has: "${variantOpt}"`);
            console.log(`    Selected:    "${selectedOpt}"`);
            console.log(`    Match: ${match ? "✅" : "❌"}`);
            if (!match) {
              console.log(
                `    Case-insensitive match: ${
                  variantOpt.toLowerCase() === selectedOpt.toLowerCase()
                    ? "YES"
                    : "NO"
                }`
              );
            }
          });
        });
      } else {
        console.log(`✅ [${timestamp}] Variant found:`, {
          id: selectedVariant.id,
          options: selectedVariant.options,
          available: selectedVariant.available,
        });
      }

      return selectedVariant || null;
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
